import { ColDef, GridApi, GridOptions, GridReadyEvent, Module } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GridTemplateRendererComponent } from '@components/grid-template-renderer/grid-template-renderer.component';
import { DropDown } from '@interfaces/drop-down.interface';
import { LogService } from '@services/log.service';

@Component({
  selector: 'app-move-users-dialog',
  templateUrl: './move-users-dialog.component.html',
  styleUrls: ['./move-users-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveUsersDialogComponent implements AfterViewInit {
  modules: Module[] = [ClientSideRowModelModule];

  public rowData: any[] = [];

  public gridOptions: GridOptions = {
    rowModelType: 'clientSide',
    suppressMovableColumns: true,
    suppressContextMenu: true,
    rowHeight: 24,
    headerHeight: 24,
    tooltipShowDelay: 0,
    tooltipMouseTrack: true,
  };
  public columnDefs: ColDef[] = [];

  public defaultColDef: ColDef = {
    resizable: false,
    sortable: false,
    filter: false,
    flex: 1,
    suppressMovable: true,
    suppressMenu: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
  };

  public orgFormVM: {
    billableOrgId: number;
    orgId: number;
    selectedBillableOrgId?: number;
    billableOrgName?: string;
  } = {
    billableOrgId: null,
    orgId: null,
  };

  private gridApi!: GridApi;
  private destinationOrg: DropDown<number> = null;

  @ViewChild('removeUserTemplate') removeUserTemplate: TemplateRef<any>;

  public onCancel = new EventEmitter<void>();
  public onContinue = new EventEmitter<{
    destinationOrg: number;
    selectedUsers: any[];
    selectedBillableOrgId: number | null;
  }>();

  constructor(
    public dialogRef: MatDialogRef<MoveUsersDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      selectedUsers: any[];
      csoBillableOrgId: number;
      csoBillableOrgName?: string;
      csoId: number;
    },
    private _logger: LogService,
    private _cdr: ChangeDetectorRef
  ) {
    this.rowData = data.selectedUsers || [];
    this.orgFormVM.billableOrgId = data.csoBillableOrgId;
    this.orgFormVM.orgId = data.csoId;
    this.orgFormVM.selectedBillableOrgId = data.csoBillableOrgId;
    this.orgFormVM.billableOrgName = data.csoBillableOrgName;
    this.destinationOrg = {
      value: data.csoId,
      text: '',
    };
    this._logger.log('MoveUsersDialogComponent', [...this.rowData]);
  }

  ngAfterViewInit(): void {
    this.configureGrid();
  }

  protected isConfirmDisabled(): boolean {
    return !this.rowData || this.rowData.length === 0 || !this.destinationOrg;
  }

  protected onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  protected onRemoveUserClick(id: any): void {
    this._logger.log('Removing user', id);
    this.removeUserFromGrid(id);
  }

  protected onCancelClick(): void {
    this.onCancel.emit();
  }

  protected onMoveUsersClick(): void {
    this._logger.trackEvent(
      'MoveUsersDialogComponent',
      'moveUsersClick',
      'MoveUsersDialogComponent',
      this.rowData.length
    );
    this.emitContinue();
  }

  private emitContinue(): void {
    if (!this.destinationOrg) {
      return;
    }
    this.onContinue.emit({
      destinationOrg: this.destinationOrg.value,
      selectedUsers: this.rowData,
      selectedBillableOrgId:
        this.orgFormVM?.selectedBillableOrgId ?? this.orgFormVM?.billableOrgId ?? null,
    });
  }

  protected onDestinationOrgChanged(selectedItem: DropDown<number> | null) {
    this.setDestinationOrg(selectedItem);
  }

  private setDestinationOrg(selectedItem: DropDown<number> | null) {
    this.destinationOrg = selectedItem ?? null;
    this._cdr.markForCheck();
  }

  private removeUserFromGrid(id: string): void {
    this.rowData = this.rowData.filter(user => user.userId !== id);
    this.gridApi.setRowData(this.rowData);
    this._cdr.markForCheck();
  }

  private configureGrid(): void {
    this.columnDefs = [
      {
        field: 'userName',
        headerName: 'Username',
        sort: 'asc',
        sortIndex: 0,
        tooltipField: 'userName',
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        sort: 'asc',
        sortIndex: 1,
        tooltipField: 'firstName',
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        sort: 'asc',
        sortIndex: 2,
        tooltipField: 'lastName',
      },
      {
        field: 'emailAddress',
        headerName: 'Email Address',
        flex: 1,
        tooltipField: 'emailAddress',
      },
      {
        field: 'organizationName',
        headerName: 'Organization',
        flex: 1,
        tooltipField: 'organizationName',
      },
      {
        field: 'userId',
        headerName: 'System ID',
        width: 120,
        tooltipField: 'userId',
      },
      {
        headerName: '',
        field: 'actions',
        width: 48,
        pinned: 'right',
        lockPosition: true,
        sortable: false,
        filter: false,
        cellRendererFramework: GridTemplateRendererComponent,
        cellRendererParams: {
          ngTemplate: this.removeUserTemplate,
        },
      },
    ];
  }
}
